import { Flex, FlexGap } from '@pancakeswap/uikit'
import { ReactNode, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { gsap, SplitText, prefersReducedMotion } from 'lib/motion'

const Title = styled.div<{ align?: string }>`
  font-size: 40px;
  font-weight: 700;
  text-align: ${({ align }) => align};
  font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
  line-height: 56px;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.colors.white};
  perspective: 600px;
`

const SubTitle = styled.div<{ align?: string }>`
  font-size: 14px;
  text-align: ${({ align }) => align};
  color: ${({ theme }) => theme.colors.textSubtle};
  line-height: 21px;
`

interface EPageHeaderProps {
  pageName: string
  subTitle?: string
  children?: ReactNode
  align?: 'left' | 'center' | 'right'
}

const EPageHeader = ({ pageName, children, subTitle = '', align = 'center', ...props }: EPageHeaderProps) => {
  const titleRef = useRef<HTMLDivElement>(null)
  const subRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!titleRef.current || prefersReducedMotion()) return undefined
    const split = new SplitText(titleRef.current, { type: 'chars' })
    const tl = gsap.timeline()
    tl.from(split.chars, {
      opacity: 0,
      y: 24,
      rotateX: -50,
      duration: 0.6,
      stagger: 0.025,
      ease: 'expo.out',
    })
    if (subRef.current) {
      tl.from(subRef.current, { opacity: 0, y: 8, duration: 0.4, ease: 'power2.out' }, '-=0.4')
    }
    return () => {
      tl.kill()
      split.revert()
    }
  }, [pageName])

  return (
    <Flex
      flexDirection="column"
      position="relative"
      alignItems={align === 'center' ? 'center' : align === 'left' ? 'flex-start' : 'flex-end'}
      my="20px"
      width="100%"
      {...props}
    >
      <Flex
        flexDirection="column"
        alignItems={align === 'center' ? 'center' : align === 'left' ? 'flex-start' : 'flex-end'}
        mb="8px"
        width="100%"
      >
        <Title ref={titleRef} align={align}>{pageName}</Title>
        <SubTitle ref={subRef} align={align}>{subTitle}</SubTitle>
      </Flex>
      {children}
    </Flex>
  )
}

export default EPageHeader
